// "use client"

import { useState } from "react";
import loginBg from "../../assets/login-bg.jpg";
import testimonialAvatar from "../../assets/testimonial-avatar.jpg";
import google from "../../assets/google.png"
import logo from "../../assets/logo.jpg"
import { Instagram, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../../AuthProvider"; 
import { toast } from "react-fox-toast";
import { Link, useNavigate } from "react-router-dom";
import { getApiBaseUrl } from '../../config/api';

// Get API base URL from centralized config
const API_BASE_URL = getApiBaseUrl();

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, googleSignIn } = useAuth();
  // const router = useRouter()
  const navigate = useNavigate();

  // Function to send custom verification email through your backend
  const sendCustomVerificationEmail = async (user) => {
    try {
      const authToken = await user.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/auth/send-verification-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            email: user.email,
            userId: user.uid,
            continueUrl: `${window.location.origin}/verify-email`,
          }),
        }
      );

      if (!response.ok) {
        // toast.error("Failed to send verification email");
      } else {
        // toast.success("Verification email sent successfully");
      }

      return await response.json();
    } catch (error) {
      console.error("Error sending verification email:", error);
      // toast.error("Error sending verification email");
      throw error;
    }
  };

  // Function to set up user with the backend API
  const setupUserWithBackend = async (user) => {
    try {
      const authToken = await user.getIdToken();
      const userId = user.uid;

      const response = await fetch(
        `${API_BASE_URL}/auth/setup-user`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-User-Id": userId,
          },
        }
      );

      if (!response.ok) {
        // toast.warning("User setup encountered a minor issue");
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Add a small delay to ensure backend has fully processed the user setup
      // This is especially important for new users where privileges need to be created
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // toast.success("Account setup complete!");
      return data;
    } catch (error) {
      console.error("Error setting up user with backend:", error);
      // toast.error("Error setting up your account");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Moved setLoading to the beginning
    try {
      const userCredential = await login(email, password, rememberMe);

      if (!userCredential.user.emailVerified) {
        await sendCustomVerificationEmail(userCredential.user);
        // toast.warning(
        //   "Please verify your email first. We've sent a verification link from Gomens."
        // );
        setLoading(false); // Ensure loading is false before returning
        return;
      }

      await setupUserWithBackend(userCredential.user);
      
      // toast.success("Login successful! Redirecting...");
      navigate("/recentactivities");
    } catch (err) {
      let errorMessage = "Login failed";

      if (
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-credential"
      ) {
        errorMessage = "Invalid email or password";
      } else if (err.code === "auth/user-not-found") {
        errorMessage = "Account not found. Please sign up.";
      } else if (err.code === "auth/too-many-requests") {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (err.code === "auth/user-disabled") {
        errorMessage = "Account disabled. Contact support.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address";
      }

      toast.error(errorMessage);
    } finally {
      // Use finally to ensure setLoading(false) is always called
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true); 
    try {
      const userCredential = await googleSignIn(rememberMe);
      await setupUserWithBackend(userCredential.user);
      
      // toast.success("Google login successful! Redirecting...");
      navigate("/recentactivities");
    } catch (err) {
      let errorMessage = "Google sign in failed";

      if (err.code === "auth/account-exists-with-different-credential") {
        errorMessage = "Email already in use. Try email/password login.";
      } else if (err.code === "auth/popup-closed-by-user") {
        errorMessage = "Sign in canceled";
      }

      toast.error(errorMessage);
    } finally {
      // Use finally to ensure setLoading(false) is always called
      setLoading(false);
    }
  };

  const avatarPlaceholder = "/placeholder.svg?height=40&width=40"; // Define once

  return (
    <div className="h-screen flex w-full">
      {/* Left Side - Dark Background with Social Media Preview */}
      <div className="hidden flex-1 bg-black max-w-xl md:flex flex-col justify-center items-center relative overflow-hidden">
        {/* Full-panel background image */}
        <img
          src={loginBg}
          alt="Gomens AI fashion"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        {/* Overlay gradient for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Testimonial Card */}
        <div className="absolute bottom-8 left-4 right-4 z-10 w-full max-w-md bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-lg">
          {" "}
          {/* Replaced Card */}
          <div className="p-6">
            {" "}
            {/* Replaced CardContent */}
            <p className="text-white text-sm leading-relaxed mb-4">
              {
                "I've used countless image tools, but nothing matches the precision and speed of this AI editor. Backgrounds, upscaling, even virtual try-ons—it just works. It's now a core part of my workflow."
              }
            </p>
            <div className="flex items-center gap-3">
              {/* Replaced Avatar, AvatarImage, AvatarFallback */}
              <div className="w-10 h-10 rounded-full bg-gray-600 flex justify-center items-center overflow-hidden">
                {avatarPlaceholder ? (
                  <img
                    src={testimonialAvatar}
                    alt="Anaya K."
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white font-medium">AK</span>
                )}
              </div>
              <div>
                <div className="text-white font-semibold text-sm">Anaya K.</div>
                <div className="text-gray-400 text-xs">
                  Founder of a Growing SaaS Startup
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex gap-2 mt-6">
          <div className="w-8 h-2 bg-indigo-600 rounded-full"></div>
          <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
          <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
          <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
        </div>
      </div>

      {/* Right Side - Dark Background with Sign In Form */}
      <div className="flex-1 bg-white p-8 w-[60vw] flex flex-col justify-center items-center">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center">
            <div className="md:w-20 md:h-20 w-28 h-28 mx-auto flex items-center justify-center">
              <img src={logo} alt="Gomens" />
            </div>
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome Back!
            </h1>
            <p className="text-gray-500">Sign in to continue to your account</p>
          </div>

          {/* Google Sign In Button */}
          <button
            type="button"
            className="w-full mb-6 h-12 border border-gray-300 bg-white rounded-md hover:bg-gray-50 flex items-center justify-center gap-3 text-gray-700 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5">
                  <img src={google} alt="" />
                </div>
                <span className="text-gray-700 font-medium">
                  Sign in with Google
                </span>
              </div>
            )}
          </button>

          {/* Divider */}
          <div className="text-center flex items-center justify-center gap-3 mb-6">
            <div className="w-[10vw] h-[0.5px] bg-gray-300"></div>
            <span className="text-gray-400">Or Sign in with</span>
            <div className="w-[10vw] h-[0.5px] bg-gray-300"></div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Email Input */}
            <div className="mb-4">
              <label htmlFor="emailInput" className="block text-gray-700 font-medium mb-2">
                Email
              </label>
              <input
                id="emailInput"
                type="email"
                placeholder="Email here.."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-600 focus:border-violet-600 sm:text-sm"
                required
              />
            </div>

            {/* Password Input */}
            <div className="mb-4">
              <label htmlFor="passwordInput" className="block text-gray-700 font-medium mb-2">
                Password
              </label>
              <input
                id="passwordInput"
                type="password"
                placeholder="Password here.."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-violet-600 focus:border-violet-600 sm:text-sm"
                required
              />
            </div>

            {/* Remember Me and Forgot Password */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 accent-violet-600 focus:ring-2 focus:ring-violet-500 focus:ring-offset-1"
                />
                <label htmlFor="remember" className="text-gray-600 text-sm cursor-pointer select-none">
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password">
                <button type="button" className="text-violet-600 p-0 h-auto cursor-pointer font-normal hover:underline focus:outline-none focus:underline">
                  Forgot Password?
                </button>
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              className="w-full h-12 cursor-pointer bg-black hover:bg-gray-900 text-white rounded-lg mb-6 font-medium transition-all focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="text-center">
            <span className="text-gray-500">{"Don't have an account yet? "}</span>
            <Link to="/signup">
              <button type="button" className="text-violet-600 p-0 h-auto font-normal cursor-pointer hover:underline focus:outline-none focus:underline">
                Sign Up
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
